import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider } from "@/context/AuthContext";
import { I18nProvider } from "@/context/I18nContext";
import ThemePrompt from "@/components/ThemePrompt";
import { Toaster } from "@/components/ui/sonner";
import ProtectedRoute from "@/components/ProtectedRoute";
import AuthCallback from "@/components/AuthCallback";
import CountyGate from "@/components/CountyGate";
import Landing from "@/pages/Landing";
import Pricing from "@/pages/Pricing";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import FormsCatalog from "@/pages/FormsCatalog";
import FormDetail from "@/pages/FormDetail";
import SavedForms from "@/pages/SavedForms";
import PaymentSuccess from "@/pages/PaymentSuccess";
import AdminPanel from "@/pages/AdminPanel";
import OnboardingBusiness from "@/pages/OnboardingBusiness";
import AttorneyDirectory from "@/pages/AttorneyDirectory";
import FormFinder from "@/pages/FormFinder";
import Legal from "@/pages/Legal";
import Profile from "@/pages/Profile";
import Counties from "@/pages/Counties";
import CountyForms from "@/pages/CountyForms";
import Matters from "@/pages/Matters";
import MatterDetail from "@/pages/MatterDetail";
import ForAttorneys from "@/pages/ForAttorneys";
import FirmLogin from "@/pages/FirmLogin";
import BundleBuilder from "@/pages/BundleBuilder";
import EmailCopy from "@/pages/EmailCopy";
import CityPage from "@/pages/CityPage";
import FeaturedSpots from "@/pages/FeaturedSpots";
import JoinDirectory from "@/pages/JoinDirectory";
import { SosGate } from "@/lib/siteConfig";

function ScrollTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <>
      <ScrollTop />
      <CountyGate />
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/bundle" element={<BundleBuilder />} />
        <Route path="/login" element={<Login />} />
        <Route path="/firm/:slug" element={<FirmLogin />} />
        <Route path="/forms" element={<FormsCatalog />} />
        <Route path="/sos-forms" element={<SosGate><FormsCatalog sosSection /></SosGate>} />
        <Route path="/find" element={<FormFinder />} />
        <Route path="/nc" element={<Counties />} />
        <Route path="/for-attorneys" element={<ForAttorneys />} />
        <Route path="/find-attorney" element={<AttorneyDirectory />} />
        <Route path="/nc/:slug" element={<CountyForms />} />
        <Route path="/nc/:slug/:city" element={<CityPage />} />
        <Route path="/es/nc" element={<Counties />} />
        <Route path="/es/nc/:slug" element={<CountyForms />} />
        <Route path="/es/nc/:slug/:city" element={<CityPage />} />
        <Route path="/fr/nc" element={<Counties />} />
        <Route path="/fr/nc/:slug" element={<CountyForms />} />
        <Route path="/fr/nc/:slug/:city" element={<CityPage />} />
        <Route path="/ar/nc" element={<Counties />} />
        <Route path="/ar/nc/:slug" element={<CountyForms />} />
        <Route path="/ar/nc/:slug/:city" element={<CityPage />} />
        <Route path="/hmn/nc" element={<Counties />} />
        <Route path="/hmn/nc/:slug" element={<CountyForms />} />
        <Route path="/hmn/nc/:slug/:city" element={<CityPage />} />
        <Route path="/featured" element={<ProtectedRoute><FeaturedSpots /></ProtectedRoute>} />
        <Route path="/join/:token" element={<JoinDirectory />} />
        <Route path="/legal/:doc" element={<Legal />} />
        <Route path="/forms/:number" element={<FormDetail />} />
        <Route path="/payment/success" element={<PaymentSuccess />} />
        <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
        <Route path="/copies/:token" element={<ProtectedRoute><EmailCopy /></ProtectedRoute>} />
        <Route path="/saved" element={<ProtectedRoute><SavedForms /></ProtectedRoute>} />
        <Route path="/matters" element={<ProtectedRoute><Matters /></ProtectedRoute>} />
        <Route path="/onboarding/business" element={<ProtectedRoute><OnboardingBusiness /></ProtectedRoute>} />
        <Route path="/matters/:id" element={<ProtectedRoute><MatterDetail /></ProtectedRoute>} />
        <Route path="/admin" element={<ProtectedRoute><AdminPanel /></ProtectedRoute>} />
      </Routes>
    </>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <HelmetProvider>
          <I18nProvider>
            <AuthProvider>
              <AppRouter />
              <ThemePrompt />
              <Toaster position="top-right" richColors />
            </AuthProvider>
          </I18nProvider>
        </HelmetProvider>
      </BrowserRouter>
    </div>
  );
}
